import * as React from 'react';
import { type Color, type Phase } from '@chess/api-model';
import type { ReviewGame } from '@chess/api-review';
import { STAGE_NAME, type OpeningMark, barScoreText, moveLabelOf, type ReaderScore, type StageHead } from './head.ts';

// The stage head as a presentational component: everything it shows comes
// in as props and every click goes out through `onGo`, so it renders the
// same inside the app and in Storybook, where the stories supply the head.
//
// Two lines and a count. First line: the title, and at the right, in the
// title's own size, the change between the stage's first and last scores,
// green in the reader's favour and red against. Second line, small: the
// move numbers of the two ends with their scores, each a link to its ply.
// Then the reader's blunders and misses as counts only.

export interface StageHeaderProps {
  phase: Phase;
  /** The opening's name for the opening tab, the stage's name otherwise. */
  title: string;
  /** Opening only: the title's tooltip, the full opening name and its ECO code. */
  titleTooltip?: string;
  /** Absent while no game is open. */
  game?: Pick<ReviewGame, 'moves'>;
  head?: StageHead;
  /** The reader's side; undefined when the owner played neither side. */
  reader?: Color;
  scanning?: { done: number; total: number };
  /**
   * Opening only, at the top right: `star` when the reader followed the theory
   * and left it with the engine's best move, `blunder` when the theory was left
   * with a blunder of the reader's.
   */
  mark?: OpeningMark;
  onGo: (ply: number) => void;
}

// The figure and its tone come from `head.ts`, computed from the same
// reader-side scores as the chips below, so the two never disagree.
const Ends: React.FC<{ head: StageHead; whose: string; onGo: (ply: number) => void }> = ({ head, whose, onGo }) => {
  const end = (label: string, ply: number, score: ReaderScore | undefined, title: string) => (
    <span className="phase-end">
      <span className="phase-end-label">{label}</span>{' '}
      <button type="button" className="phase-end-link" onClick={() => onGo(ply)} title={title}>
        {moveLabelOf(ply)}
      </button>{' '}
      <span className="score-chip" title={score ? `Score, from ${whose} side` : 'Not scanned yet'}>
        {score ? barScoreText(score) : '…'}
      </span>
    </span>
  );
  return (
    <p className="phase-ends">
      {end('from', Math.min(head.fromPly + 1, head.toPly), head.start, `The stage's first move; the score it started from, from ${whose} side · go there`)}
      <span className="phase-dot" aria-hidden="true">
        {' · '}
      </span>
      {end('to', head.endPly, head.end, `The stage's last move, where its score is read, from ${whose} side · go there`)}
    </p>
  );
};

const Counts: React.FC<{ head: StageHead; scanning?: { done: number; total: number } }> = ({ head, scanning }) => {
  const n = (count: number, word: string, plural = `${word}s`) => `${count} ${count === 1 ? word : plural}`;
  const partial = head.judged > 0 && head.judged < head.readerMoves ? `${head.judged} of ${head.readerMoves} moves judged` : undefined;
  let text: string;
  if (head.readerMoves === 0) text = 'No moves of yours in this stage.';
  else if (head.judged > 0) {
    // Only the counters that are not zero; no line at all when both are.
    text = [
      head.blunders.length > 0 ? n(head.blunders.length, 'blunder') : undefined,
      head.misses.length > 0 ? n(head.misses.length, 'miss', 'misses') : undefined,
    ]
      .filter(Boolean)
      .join(' · ');
    if (!text && !partial) return null;
  } else text = scanning?.total ? `Scanning · ${scanning.done}/${scanning.total}` : 'Not scanned yet.';
  return (
    <p className="phase-counts">
      {text}
      {head.judged > 0 && partial && <span className="phase-partial">{text ? ' · ' : ''}{partial}</span>}
    </p>
  );
};

const STAR =
  'M234.5 114.38l-45.1 39.36 13.51 58.6a16 16 0 0 1-23.84 17.34l-51.11-31-51 31a16 16 0 0 1-23.84-17.34l13.49-58.54-45.11-39.42a16 16 0 0 1 9.11-28.06l59.46-5.15 23.21-55.36a15.95 15.95 0 0 1 29.44 0L166 81.17l59.44 5.15a16 16 0 0 1 9.11 28.06z';
const X_CIRCLE =
  'M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm37.66,130.34a8,8,0,0,1-11.32,11.32L128,139.31l-26.34,26.35a8,8,0,0,1-11.32-11.32L116.69,128,90.34,101.66a8,8,0,0,1,11.32-11.32L128,116.69l26.34-26.35a8,8,0,0,1,11.32,11.32L139.31,128Z';

/** The opening's mark at the top right: a green star (Phosphor "star") or a red blunder mark (Phosphor "x-circle"), both filled. */
const Mark: React.FC<{ mark: OpeningMark }> = ({ mark }) => {
  const label = mark === 'star' ? 'Played the theory and left it with the best move' : 'Left the theory with a blunder';
  return (
    <span className={`phase-mark phase-mark-${mark}`} role="img" aria-label={label} title={label}>
      <svg width="12" height="12" viewBox="0 0 256 256" aria-hidden="true" focusable="false">
        <path fill="currentColor" d={mark === 'star' ? STAR : X_CIRCLE} />
      </svg>
    </span>
  );
};

export const StageHeader: React.FC<StageHeaderProps> = ({ phase, title, titleTooltip, game, head, reader, scanning, mark, onGo }) => {
  if (!game || !head) {
    return (
      <>
        <h2>{STAGE_NAME[phase]}</h2>
        <p className="panel-empty">No game open.</p>
      </>
    );
  }
  const whose = reader ? 'your' : 'White’s';
  return (
    <header className="phase-head">
      <div className="phase-title-row">
        <h2 title={titleTooltip}>
          {title}
        </h2>
        {mark && <Mark mark={mark} />}
      </div>
      {head.ended ? (
        <p className="panel-empty phase-ended">This game ended before the {STAGE_NAME[phase].toLowerCase()}.</p>
      ) : (
        <>
          <Ends head={head} whose={whose} onGo={onGo} />
          <Counts head={head} scanning={scanning} />
        </>
      )}
    </header>
  );
};
